/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */

import apiClient from './client';
import { useAuthStore } from '@/store/authStore';
import { useAppStore } from '@/store/appStore';

export interface PageHelpCard {
  helpKey: string;
  /** Language actually served — may be 'en' when the user's language has no row yet. */
  language: string;
  title: string;
  summary?: string | null;
  /** Markdown: numbered steps plus warnings. */
  body: string;
  isMachineTranslated: boolean;
}

/** A page the answer offers to open. The route is resolved locally from HELP_KEY_ROUTES. */
export interface PageHelpLink {
  helpKey: string;
  /** The page's name in the user's language — the button label. */
  title: string;
  /** What to put in that page's search box on arrival (a driver's name, a plate). Usually absent. */
  search?: string | null;
}

/**
 * A send the assistant has prepared but has NOT performed. The panel shows it and asks; only pressing
 * the button sends. Contacts are included because a person cannot confirm a recipient they cannot see.
 */
export interface PageHelpPendingAction {
  id: string;
  /** 'card_link' | 'rental_agreement' */
  kind: string;
  driverName: string;
  phone?: string | null;
  email?: string | null;
  vehicle: string;
  dates: string;
  /** 'default' | 'sms' | 'email' | 'both' */
  channel: string;
}

/** A page an answer was read from, when it came from the web rather than from the help text. */
export interface PageHelpSource {
  url: string;
  title: string;
}

export interface PageHelpAnswer {
  answer: string;
  /** False when the question is not covered by the portal's help text. */
  grounded: boolean;
  /** Pages worth opening for this question. */
  links?: PageHelpLink[];
  /** The page the answer is mostly about, when it is not the current one. */
  helpKey?: string | null;
  /** Served from the learned FAQ without a model call, so the UI can offer a re-ask. */
  fromCache?: boolean;
  /**
   * The question was about the caller's own records (their bookings, tolls, invoices) and they are not
   * signed in. The assistant never reads anyone's data — it points the owner at the page that holds it.
   */
  needsSignIn?: boolean;
  /** The caller hit the anonymous hourly quota. Signed-in users are never throttled. */
  rateLimited?: boolean;
  /** Set when the answer prepared a send that is waiting for the reader to allow it. */
  pendingAction?: PageHelpPendingAction | null;
  /**
   * Pages the answer was read from. Present only when the portal's own help text did not cover the
   * question and the assistant searched the agencies' sites instead — an answer built from somebody
   * else's page is shown with that page beside it.
   */
  sources?: PageHelpSource[];
}

export interface PageHelpAdminRow {
  helpKey: string;
  title: string;
  summary?: string | null;
  body: string;
  isActive: boolean;
  updatedAt: string;
  /** Languages translated from the CURRENT English text. */
  translatedLanguages: string[];
  /** Languages translated from an OLDER English text. */
  staleLanguages: string[];
}

/** One entry of the learned FAQ, as the admin editor sees it. */
export interface LearnedAnswer {
  id: string;
  language: string;
  question: string;
  answer: string;
  /** Every wording that finds this answer: the question as typed plus the learned paraphrases. */
  phrases: string[];
  links: string[];
  originHelpKey?: string | null;
  /** How many times this answer was served without calling the model. */
  useCount: number;
  isAdminEdited: boolean;
  isBlocked: boolean;
  /** The help cards changed after this was written, so it is no longer served. */
  isStale: boolean;
  lastUsedAt: string;
  createdAt: string;
}

export const pageHelpApi = {
  /**
   * The help card for one page. Resolves to null when the page has no card yet (the API answers 204),
   * so the caller simply hides the help button instead of showing an empty panel.
   */
  async get(helpKey: string, lang: string): Promise<PageHelpCard | null> {
    const res = await apiClient.get<PageHelpCard | ''>(
      `/page-help/${encodeURIComponent(helpKey)}`,
      { params: { lang } },
    );
    if (res.status === 204 || !res.data) return null;
    return res.data as PageHelpCard;
  },

  /**
   * Ask about anything in the portal. `helpKey` is the page the user is looking at — context for the
   * answer, not a limit on it: the assistant reads every page's help text and may point elsewhere.
   * `forceRefresh` skips the learned FAQ, which is what the "answer again" button sends.
   */
  async ask(
    helpKey: string,
    question: string,
    language: string,
    forceRefresh = false,
  ): Promise<PageHelpAnswer> {
    const res = await apiClient.post<PageHelpAnswer>('/page-help/ask', {
      question, language, helpKey, forceRefresh,
    });
    return res.data;
  },

  /**
   * The same question, answered as it is written.
   *
   * `onDelta` receives the answer text in pieces, `onReset` means a second attempt is replacing what was
   * shown, and the promise resolves with the finished answer — which is what should be rendered in the end,
   * because it carries the links and the flags the deltas do not.
   *
   * Uses fetch rather than the axios client: axios buffers the whole response, and buffering is the one
   * thing this endpoint exists to avoid. That means re-doing by hand what the client's interceptor does —
   * the bearer token and, for an admin looking at somebody else's account, the impersonation header the
   * answer's data snapshot is scoped by.
   */
  async askStream(
    helpKey: string,
    question: string,
    language: string,
    forceRefresh: boolean,
    handlers: { onDelta: (text: string) => void; onReset: () => void },
    signal?: AbortSignal,
  ): Promise<PageHelpAnswer> {
    const token = useAuthStore.getState().token;
    const { selectedOwner } = useAppStore.getState();
    const authUser = useAuthStore.getState().user;
    const impersonating = selectedOwner && authUser && selectedOwner.id !== authUser.id
      ? selectedOwner.id
      : null;

    const res = await fetch('/api/page-help/ask/stream', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(impersonating ? { 'X-Owner-Id': String(impersonating) } : {}),
      },
      body: JSON.stringify({ question, language, helpKey, forceRefresh }),
      signal,
    });
    if (!res.ok || !res.body) throw new Error(`stream failed: ${res.status}`);

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let done: PageHelpAnswer | null = null;
    let failure: string | null = null;

    // SSE frames are separated by a blank line; a frame can arrive in pieces, so only whole frames are cut
    // off the buffer and the remainder waits for the next chunk.
    for (;;) {
      const { value, done: finished } = await reader.read();
      if (finished) break;
      buffer += decoder.decode(value, { stream: true });

      let split = buffer.indexOf('\n\n');
      while (split !== -1) {
        const frame = buffer.slice(0, split);
        buffer = buffer.slice(split + 2);
        split = buffer.indexOf('\n\n');

        const event = /^event: (.+)$/m.exec(frame)?.[1]?.trim();
        const dataLine = /^data: (.*)$/m.exec(frame)?.[1];
        if (!event || dataLine === undefined) continue;

        let data: any;
        try { data = JSON.parse(dataLine); } catch { continue; }

        if (event === 'delta' && typeof data.text === 'string') handlers.onDelta(data.text);
        else if (event === 'reset') handlers.onReset();
        else if (event === 'done') done = data as PageHelpAnswer;
        else if (event === 'error') failure = data.message || 'error';
      }
    }

    if (done) return done;
    throw new Error(failure || 'stream ended without an answer');
  },

  /**
   * Tell the server which suggested page the reader actually opened. Best-effort: the caller ignores
   * failures, because this improves the NEXT answer and must never delay this one's navigation.
   */
  async recordNavClick(question: string, language: string, helpKey: string): Promise<void> {
    await apiClient.post('/page-help/nav-click', { question, language, helpKey });
  },

  /**
   * Allow a prepared send to go out. This is the only call in the help panel that reaches a real
   * person, which is why it exists as a separate, deliberate press rather than a step the answer takes
   * on its own.
   */
  async confirmAction(actionId: string): Promise<{ sent: boolean; message: string }> {
    const res = await apiClient.post<{ sent: boolean; message: string }>(
      '/page-help/action/confirm', { actionId },
    );
    return res.data;
  },

  /** Admin editor: every English card plus its per-language coverage. */
  async list(): Promise<PageHelpAdminRow[]> {
    const res = await apiClient.get<PageHelpAdminRow[]>('/page-help');
    return res.data || [];
  },

  async save(
    helpKey: string,
    data: { title: string; summary?: string | null; body: string; isActive: boolean },
  ): Promise<void> {
    await apiClient.put(`/page-help/${encodeURIComponent(helpKey)}`, data);
  },

  /** Fill every other language from the English card. Returns how many languages were written. */
  async translate(helpKey: string): Promise<number> {
    const res = await apiClient.post<{ translated: number }>(
      `/page-help/${encodeURIComponent(helpKey)}/translate`,
    );
    return res.data?.translated ?? 0;
  },

  /** Admin: the learned FAQ — answers already produced, and how often each has since been reused. */
  async listLearned(params: { lang?: string; search?: string } = {}): Promise<LearnedAnswer[]> {
    const res = await apiClient.get<LearnedAnswer[]>('/page-help/qa', { params });
    return res.data || [];
  },

  /** Admin: correct an answer (it becomes canonical) or block it. */
  async saveLearned(id: string, data: { answer?: string; isBlocked?: boolean }): Promise<void> {
    await apiClient.put(`/page-help/qa/${id}`, data);
  },

  /** Admin: forget an answer. The next person to ask pays for a fresh one. */
  async deleteLearned(id: string): Promise<void> {
    await apiClient.delete(`/page-help/qa/${id}`);
  },
};

export default pageHelpApi;
